<script setup lang="ts">
// #737: セクション文脈に静置する引用カード（大人の声）。主張の隣に証明を置く proof-adjacent 配置。
// 声の本文は testimonials.ts を単一ソースに id 参照する（差し替えは id 一語で済む）。
//
// デザイン（Fable レビュー案A → 流れ改善版）:
//  - 地は暖色パーチメント（#FBF8F2）＋声は Noto Serif JP＋大きな装飾「＋ミント署名フッター
//  - ただし「道に大岩がゴロゴロ」問題への対処として、セクション級の重さを剥がして"傍注"に降格:
//    ・ドロップ影の lift を廃止（接地影のみ）＝浮いた岩→路面に埋めた飛び石
//    ・幅 500px に絞る＝本体と同格の"関門"にしない
//    ・margin を非対称（上を詰め下を空ける）＝上のセクションの締めに接着し、継ぎ目の孤児にしない
//  - 枚数は 3 枚に間引き（紙後 / ゆりかご後 / 料金後）。テンプレは全枚数で完全統一（差異は属性文言だけ）
import { testimonials } from '@/data/testimonials'

interface Props {
  id: string
}
const props = defineProps<Props>()

const voice = computed(() => testimonials.find(t => t.id === props.id))
</script>

<template>
  <figure
    v-if="voice"
    class="voice-card"
  >
    <blockquote class="voice-quote">
      {{ voice.quote }}
    </blockquote>
    <figcaption class="voice-author">
      {{ voice.author }}
    </figcaption>
  </figure>
</template>

<style scoped>
.voice-card {
  position: relative;
  max-width: 500px;
  /* 非対称マージン: 上を詰めて「上のセクションの締め」に接着し、下を大きく空けて次と離す */
  margin: 12px auto clamp(48px, 7vw, 76px);
  padding: 26px 30px 0;
  background: #FBF8F2;
  border: 1px solid var(--landing-color-border-mint);
  border-radius: var(--landing-radius);
  /* 接地影のみ。lift(0 10px 26px)は廃止して「浮いた岩」感を消す */
  box-shadow: 0 1px 2px rgba(36, 52, 71, 0.05);
  color: var(--landing-color-text);
  overflow: hidden;
}

/* 焦点になる装飾引用符（和・雑誌感）。左太罫を廃してこれに主役を移す */
.voice-card::before {
  content: "「";
  position: absolute;
  top: 2px;
  left: 12px;
  font-family: "Noto Serif JP", serif;
  font-size: 3.2rem;
  line-height: 1;
  color: var(--landing-color-teal);
  opacity: 0.24;
  pointer-events: none;
}

.voice-quote {
  position: relative;
  margin: 0;
  font-family: "Noto Serif JP", serif;
  font-size: clamp(1rem, 1.2vw, 1.1rem);
  line-height: 1.9;
  font-weight: 400;
  letter-spacing: 0.01em;
}

/* 属性を"祝う"署名フッター帯（匿名属性なので頭文字アバターは使わない） */
.voice-author {
  margin: 20px -30px 0;
  padding: 11px 30px;
  background: var(--landing-color-card-mint);
  border-top: 1px solid var(--landing-color-border-mint);
  color: var(--landing-color-teal-darker);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-align: right;
}

.voice-author::before {
  content: "— ";
  color: var(--landing-color-teal);
}

@media (max-width: 600px) {
  .voice-card {
    padding: 22px 22px 0;
  }

  .voice-author {
    margin: 18px -22px 0;
    padding: 10px 22px;
  }
}
</style>
